import { GraphQLFetchPolicy } from '@getaccept/lib-shared/src/graphql/graphql-fetch-policy';
import type { DocumentListItem } from '@getaccept/lib-shared/src/types/document-list-item';
import { capitalize } from '@getaccept/lib-shared/src/helpers';
import { DocumentFilter } from '@getaccept/lib-shared/src/enums/document-filter';
import type { DocumentNotificationTransferMode } from '@getaccept/lib-shared/src/enums/document-notification-transfer-mode';
import type { UpdateDocumentPatch } from '@getaccept/lib-shared/src/types/update-document-patch';
import type { DocumentPage } from '@getaccept/lib-shared/src/types/document-page';
import { apolloClientInternal } from '@getaccept/lib-shared/src/internal/apollo-client-internal';
import type { Filter } from '@getaccept/lib-shared/src/types/filter';
import { DocumentsMutations } from '../graphql/documents.mutations';
import { DocumentsQueries } from '../graphql/documents.queries';
import type { PresignedLinkDTO } from '../types/pre-signed-link-dto';
import type { ArchiveDocumentsArgsInput } from '../types/archive-documents-args-input';
import type { UpdateDocumentArgs } from '../types/update-document-args';

export class DocumentsService {
  static async getMany(filter: Filter): Promise<DocumentListItem[]> {
    const documentFilter =
      filter.documentFilter === DocumentFilter.Recalled
        ? 'Deleted'
        : capitalize(filter.documentFilter);
    const tagIds = filter.tagIds?.length ? filter.tagIds : undefined;

    const {
      data: { getDocuments },
    } = await apolloClientInternal.query({
      query: DocumentsQueries.getMany,
      variables: {
        ...filter,
        documentFilter,
        tagIds,
        orderBy: filter.orderBy?.sortBy
          ? `${filter.orderBy.sortBy}:${filter.orderBy.sortOrder}`
          : undefined,
      },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return getDocuments;
  }

  static async getDocumentPage(documentId: string, id: string): Promise<DocumentPage> {
    const {
      data: { documentPage },
    } = await apolloClientInternal.query({
      query: DocumentsQueries.getDocumentPage,
      variables: { documentId, id },
      fetchPolicy: GraphQLFetchPolicy.NetworkOnly,
    });
    return documentPage;
  }

  static async sortDocumentPages(
    documentId: string,
    documentPageIds: string[],
    pageNums: number[]
  ): Promise<DocumentPage[]> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.sortPages,
      variables: {
        documentId,
        documentPageIds,
        pageNums,
      },
    });
    return data.sortDocumentPages;
  }

  static async deletePage(
    documentId: string,
    documentPageId: string,
    deletedPricingTableIds: string[]
  ): Promise<boolean> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.deletePage,
      variables: { documentId, documentPageId, deletedPricingTableIds },
    });
    return data;
  }

  static async updateDocuments({
    patch,
    transferMode,
    documentIds,
  }: UpdateDocumentArgs): Promise<Document[]> {
    Object.keys(patch).forEach(key => {
      if (patch[key] === null) {
        delete patch[key];
      }
    });
    const {
      data: { updateDocuments },
    } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.updateMany,
      variables: {
        patch,
        settings: { transferMode },
        documentIds,
      },
    });
    return updateDocuments;
  }

  static updateDocument = async (
    patch: UpdateDocumentPatch,
    documentId: string,
    transferMode?: DocumentNotificationTransferMode
  ): Promise<boolean> => {
    const {
      data: { updateDocument },
    } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.update,
      variables: {
        patch,
        settings: { transferMode },
        documentId,
      },
    });
    return updateDocument;
  };

  static async getPresignedLink(payload: ArchiveDocumentsArgsInput[]): Promise<PresignedLinkDTO[]> {
    const {
      data: { archiveDocuments },
    } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.getPresignedLinks,
      variables: { payload },
    });
    return archiveDocuments;
  }

  static async uploadContracts(
    args: {
      documentId: string;
      index: number;
      uniqueFilenames: string[];
    }[]
  ): Promise<boolean> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.uploadFilesDone,
      variables: { args, jobType: 'Archive' },
    });
    return data;
  }

  static async updateDocumentsInFolder(folderId: string, documentIds: string[]): Promise<void> {
    const {
      data: { bulkMutationResponse },
    } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.updateInFolders,
      variables: { documentIds, folderId },
    });
    return bulkMutationResponse;
  }

  static async deleteDocuments(ids: string[]): Promise<boolean> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.delete,
      variables: { ids },
    });
    return data;
  }

  static async deleteDocumentsFromFolder(
    folderId: string,
    documentIds: string[]
  ): Promise<boolean> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.deleteFromFolder,
      variables: {
        folderId,
        documentIds,
      },
    });
    return data;
  }

  static async updateDocumentTags(documentIds: string[], tagIds: string[]): Promise<string[]> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.updateDocumentTags,
      variables: { documentIds, tagIds },
    });
    return data;
  }

  static async removeDocumentTags(documentIds: string[], tagIds: string[]): Promise<string[]> {
    const { data } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.removeTagsMutation,
      variables: { documentIds, tagIds },
    });
    return data;
  }

  static async downloadDocuments(documentIds: string[]): Promise<boolean> {
    const {
      data: { batchDownloadDocuments },
    } = await apolloClientInternal.mutate({
      mutation: DocumentsMutations.downloadDocuments,
      variables: { documentIds },
    });
    return batchDownloadDocuments;
  }

  static async downloadDocument(documentId: string): Promise<void> {
    await apolloClientInternal.mutate({
      mutation: DocumentsMutations.downloadDocument,
      variables: {
        documentId,
      },
    });
  }
}
